import { useTranslate } from "@similarweb/i18n-provider";
import type { ConversionRatesCategoryOverviewPopListParams } from "@similarweb/pro-api-client";
import { Flex } from "@similarweb/ui-components/foundations";

import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";

import { CONVERSION_RATE_TRANSLATION_KEYS, conversionRateFormatters } from "../../consts";
import { ConversionRateSectionHeader } from "../../shared/ConversionRateSectionHeader";
import { resolveCardState } from "../../shared/resolveCardState";

import { AVG_RATE_AUTOMATION_IDS, CONVERTED_VISITS_AUTOMATION_IDS } from "./automationIds";
import { ConversionRateOverviewPopCard } from "./ConversionRateOverviewPopCard";
import { useConversionRateOverviewPopData } from "./useConversionRateOverviewPopData";

interface IConversionRateOverviewPopCardsProps {
    primaryCategory: ICategory | ICustomCategory;
    availabilityState: "available" | "locked";
    country: number;
    countryText: string;
    includeSubdomains: boolean;
    from: string;
    to: string;
    compareFrom: string;
    compareTo: string;
    isWindow: boolean | undefined;
    dateRangeText: string;
    currentRowLabel: string;
    comparedRowLabel: string;
    changeSuffix: string;
}

export const ConversionRateOverviewPopCards = ({
    primaryCategory,
    availabilityState,
    country,
    countryText,
    includeSubdomains,
    from,
    to,
    compareFrom,
    compareTo,
    isWindow,
    dateRangeText,
    currentRowLabel,
    comparedRowLabel,
    changeSuffix,
}: IConversionRateOverviewPopCardsProps) => {
    const { translate } = useTranslate();

    const apiParams = {
        category: primaryCategory.forApi,
        country: country,
        includeSubdomains: includeSubdomains,
        from,
        to,
        compareFrom,
        compareTo,
        isWindow,
    } satisfies ConversionRatesCategoryOverviewPopListParams;

    const { data, isLoading } = useConversionRateOverviewPopData(apiParams, {
        enabled: availabilityState === "available",
    });

    if (availabilityState === "available" && !isLoading && !data) {
        return null;
    }

    const cardState = resolveCardState(availabilityState, isLoading);

    return (
        <Flex
            flexDirection="column"
            gap="16px"
            margin="8px 0 0 0"
            data-automation="conversion-rate-pop-section"
        >
            <ConversionRateSectionHeader
                categoryDomainCount={availabilityState === "available" ? data?.categoryDomainCount : undefined}
            />
            <Flex
                gap="24px"
                width="100%"
            >
                <ConversionRateOverviewPopCard
                    automationIds={CONVERTED_VISITS_AUTOMATION_IDS}
                    title={translate(CONVERSION_RATE_TRANSLATION_KEYS.totalConvertedVisitsTitle)}
                    tooltip={translate(CONVERSION_RATE_TRANSLATION_KEYS.totalConvertedVisitsTooltip)}
                    dateRangeText={dateRangeText}
                    countryCode={country}
                    countryText={countryText}
                    state={cardState}
                    comparedRowLabel={comparedRowLabel}
                    currentRowLabel={currentRowLabel}
                    originalValue={data?.totalConvertedVisits.originalValue}
                    comparedValue={data?.totalConvertedVisits.comparedValue}
                    change={data?.totalConvertedVisits.change}
                    changeSuffix={changeSuffix}
                    formatValue={conversionRateFormatters.convertedVisits}
                    formatChange={conversionRateFormatters.visitsChange}
                />
                <ConversionRateOverviewPopCard
                    automationIds={AVG_RATE_AUTOMATION_IDS}
                    title={translate(CONVERSION_RATE_TRANSLATION_KEYS.avgConversionRateTitle)}
                    tooltip={translate(CONVERSION_RATE_TRANSLATION_KEYS.avgConversionRateTooltip)}
                    dateRangeText={dateRangeText}
                    countryCode={country}
                    countryText={countryText}
                    state={cardState}
                    comparedRowLabel={comparedRowLabel}
                    currentRowLabel={currentRowLabel}
                    originalValue={data?.avgConversionRate.originalValue}
                    comparedValue={data?.avgConversionRate.comparedValue}
                    change={data?.avgConversionRate.change}
                    changeSuffix={changeSuffix}
                    formatValue={conversionRateFormatters.avgConversionRate}
                    formatChange={conversionRateFormatters.rateChange}
                />
            </Flex>
        </Flex>
    );
};
